// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, Card, CardActionArea, CardContent, Drawer, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ArrowForwardIosOutlinedIcon from "@material-ui/icons/ArrowForwardIosOutlined";
import CloseIcon from "@material-ui/icons/Close";

// Core
import { useRouter } from "Core/Base";
import { Loader } from "Core/Components/Loader";
import { isEmptyOrWhitespace } from "Core/Utils/Utils";
import { formatSalesStatus, formatSalesStatusLite } from "Core/Utils/Format/FormatSaleStaus";

// Custom Components
import { Error } from "Custom/Components/Error";
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";
import DialogModal from "Custom/Components/Popups/DialogModal";
import ProgressView from "./DashboardSubViews/ProgressView";
import { Inspection } from "./DashboardSubViews/Inspection";

// Custom ViewModels

import { ProgressViewModel } from "Custom/ViewModels/DashboardSubViewModels/ProgressViewModel";
import { PropertyViewModel } from "Custom/ViewModels/DashboardSubViewModels/PropertyViewModel";

// Custom Styles
import { DashboardPageStyles, MainPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";

// Custom SVG Components and image
import { ReactComponent as Brickwall } from "../Content/brickwall.svg";
import { ReactComponent as Keys } from "../Content/keys.svg";
import { ReactComponent as Contract } from "../Content/contract.svg";
import { ReactComponent as Legal } from "../Content/legal.svg";
import { ReactComponent as Home } from "../Content/home.svg";
import { ReactComponent as Touch } from "../Content/touch.svg";
import { ReactComponent as CircleRightArrow } from "../Content/substraction.svg";
import { ReactComponent as Bed } from "../Content/bed.svg";
import { ReactComponent as Shower } from "../Content/shower.svg";
import { ReactComponent as Parking } from "../Content/Parking.svg";
import { ReactComponent as Tool } from "../Content/tool.svg";
import { ReactComponent as Document } from "../Content/document.svg";
import { ReactComponent as Phone } from "../Content/Phone.svg";
import { ReactComponent as Globe } from "../Content/globe.svg";
import { DashboardViewModel } from "Custom/ViewModels/DashboardViewModel";
import { WarrantyLogo } from "Custom/Components/Image/WarrantyProviderLogo";

export const Dashboard: React.FC = () => {
    //#region: Code Behind
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const dashboardPageStyles = DashboardPageStyles();
    const mainPageStyles = MainPageStyles();
    const pageAnimationStyles = usePageAnimationStyles();
    const propertyId = (router.match.params as any).propertyId;

    // Use the shared viewModel
    const [viewModel] = useState(() => new DashboardViewModel(propertyId));
    const [viewModelProperty] = useState(() => new PropertyViewModel(propertyId));

    const [viewModelProgress] = useState(() => new ProgressViewModel(propertyId));

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [openBuildDialog, setOpenBuildDialog] = useState<boolean>(false);

    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [buildDrawerOpen, setBuildDrawerOpen] = useState<boolean>(false);
    const [isDropdownOpen, setDropdownOpen] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget.parentElement!.parentElement!);
        setDropdownOpen(!isDropdownOpen);
    };

    const handleItemClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        setAnchorEl(null);
        viewModel.changePlot(id);
        setDropdownOpen(false);
    };

    const handleStatusModal = (value: boolean) => {
        isDesktop ? setOpenDialog(value) : setDrawerOpen(value);
    };

    const handleBuildStatusModal = (value: boolean) => {
        isDesktop ? setOpenBuildDialog(value) : setBuildDrawerOpen(value);
    };

    const mouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
    };

    const handleRedirectToPayments = () => {
        router.history.push(`/payment/${propertyId}`);
    };

    const handleRedirectToSnags = () => {
        router.history.push(`/snags/${propertyId}`);
    };
    // #endregion: Code Behind

    const renderBusy = () => <Loader loadingText="Fetching dashboard..." />;

    const renderError = () => <Error errorText="Failed to retrieve dashboard information." />;

    const renderPropertyItemCardContentDesktop = (title: string) => {
        return (
            <CardContent
                className={mainPageStyles.content}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    height: "100%",
                    alignItems: viewModelProperty.Description ? "flex-start" : "center",
                }}
            >
                <Box className={mainPageStyles.detailsSection} style={{ flex: "1" }}>
                    <Box className={mainPageStyles.contentTop}>
                        <Box className={mainPageStyles.propertyTitleRow} onClick={e => handleClick(e)} onMouseDown={e => mouseDown(e)}>
                            <Box className={dashboardPageStyles.titleParent}>
                                <Typography className={mainPageStyles.title}>{title}</Typography>

                                {viewModel.availablePlotOptions.length > 1 && (
                                    <Box className={mainPageStyles.dropdownArrow} onClick={e => handleClick(e)}>
                                        <ArrowForwardIosOutlinedIcon />
                                    </Box>
                                )}
                                {isDropdownOpen && (
                                    <Box
                                        style={{
                                            position: "absolute",
                                            background: "white",
                                            boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                                            width: "200px",
                                            borderRadius: "8px",
                                            padding: "10px",
                                            top: "22px",
                                            right: "0",
                                            overflowY: "auto",
                                            maxHeight: "230px",
                                        }}
                                    >
                                        {viewModel.availablePlotOptions.map((plot, index) => (
                                            <div
                                                key={index}
                                                onClick={e => handleItemClick(e, plot.id)}
                                                style={{
                                                    padding: "10px",
                                                    cursor: "pointer",
                                                    backgroundColor: plot.id === viewModel.getCurrentPropertyId ? "#f0f0f0" : "white",
                                                    borderBottom: index !== viewModel.availablePlotOptions.length - 1 ? "1px solid #ddd" : "none",
                                                }}
                                            >
                                                {`${plot.plotNumber} ${plot.developmentName}`}
                                            </div>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                            <Typography className={mainPageStyles.propertySubTitle}>{viewModel.Summary.propertyAddress}</Typography>
                        </Box>

                        <Box className={mainPageStyles.navigateToArrow}>
                            <CircleRightArrow />
                        </Box>
                    </Box>

                    <Box className={mainPageStyles.contentBottom}>
                        <Typography className={mainPageStyles.contentBottomText}>{viewModel.Summary.houseTypeName}</Typography>
                        <Box className={mainPageStyles.contentBottomList}>
                            <Box className={mainPageStyles.contentBottomItem}>
                                <Bed /> {viewModel.Summary.numberOfBeds}
                            </Box>
                            <Box className={mainPageStyles.contentBottomItem}>
                                <Shower />
                                {viewModel.Summary.numberOfBathrooms}
                            </Box>
                            <Box className={mainPageStyles.contentBottomItem}>
                                <Parking /> {viewModel.Summary.parkingSpaceCount}
                            </Box>
                        </Box>
                    </Box>
                    <Box className={mainPageStyles.contentDescription} dangerouslySetInnerHTML={{ __html: viewModelProperty.Description }} />
                </Box>
            </CardContent>
        );
    };

    const renderPropertyItemCardContentMobile = (title: string) => {
        return (
            <CardContent className={mainPageStyles.content}>
                <Box className={mainPageStyles.contentTop}>
                    <Box className={mainPageStyles.propertyTitleRow} onClick={e => handleClick(e)} onMouseDown={e => mouseDown(e)}>
                        <Box className={dashboardPageStyles.titleParent}>
                            <Typography className={mainPageStyles.title}>{title}</Typography>
                            {viewModel.availablePlotOptions.length > 1 && (
                                <Box className={mainPageStyles.dropdownArrow}>
                                    <ArrowForwardIosOutlinedIcon />
                                </Box>
                            )}
                        </Box>
                        <Typography className={mainPageStyles.propertySubTitle}>{viewModel.Summary.propertyAddress}</Typography>
                    </Box>
                    {isDropdownOpen && (
                        <Box
                            style={{
                                position: "absolute",
                                background: "white",
                                boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                                width: "200px",
                                borderRadius: "8px",
                                padding: "10px",
                                top: "44px",
                                left: "130px",
                                overflowY: "auto",
                                maxHeight: "230px",
                            }}
                        >
                            {viewModel.availablePlotOptions.map((plot, index) => (
                                <div
                                    key={index}
                                    onClick={e => handleItemClick(e, plot.id)}
                                    style={{
                                        padding: "10px",
                                        cursor: "pointer",
                                        backgroundColor: plot.id === viewModel.getCurrentPropertyId ? "#f0f0f0" : "white",
                                        borderBottom: index !== viewModel.availablePlotOptions.length - 1 ? "1px solid #ddd" : "none",
                                    }}
                                >
                                    {`${plot.plotNumber} ${plot.developmentName}`}
                                </div>
                            ))}
                        </Box>
                    )}
                    <Box className={mainPageStyles.navigateToArrow}>
                        <CircleRightArrow />
                    </Box>
                </Box>
                <Box className={mainPageStyles.contentBottom}>
                    <Typography className={mainPageStyles.contentBottomText}>{viewModel.Summary.houseTypeName}</Typography>
                    <Box className={mainPageStyles.contentBottomList}>
                        <Box className={mainPageStyles.contentBottomItem}>
                            <Bed /> {viewModel.Summary.numberOfBeds}
                        </Box>
                        <Box className={mainPageStyles.contentBottomItem}>
                            <Shower />
                            {viewModel.Summary.numberOfBathrooms}
                        </Box>
                        <Box className={mainPageStyles.contentBottomItem}>
                            <Parking /> {viewModel.Summary.parkingSpaceCount}
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        );
    };

    const renderPropertyItem = (image: string, title: string, subTitle: string, propertyItemPathname: string, thumbnail: string) => {
        const hasImage = !isEmptyOrWhitespace(image);
        return (
            <>
                {isDesktop ? (
                    <Card
                        style={{ borderRadius: 0 }}
                        className={`${mainPageStyles.navigateToPropertyItem} ${
                            router.location.pathname === propertyItemPathname || router.location.pathname === "/" ? "active" : ""
                        }`}
                    >
                        {hasImage ? (
                            <Box className={mainPageStyles.imageSection}>
                                <PrivateBlob className={mainPageStyles.imageCard} src={image.split(".").pop() !== "pdf" ? image : thumbnail} title={subTitle} cardMedia={true} />
                            </Box>
                        ) : (
                            <React.Fragment />
                        )}

                        <CardActionArea onClick={() => router.history.push(propertyItemPathname)}>{renderPropertyItemCardContentDesktop(title)}</CardActionArea>
                    </Card>
                ) : (
                    <Card
                        className={`${mainPageStyles.navigateToPropertyItem} ${
                            router.location.pathname === propertyItemPathname || router.location.pathname === "/" ? "active" : ""
                        }`}
                        style={{ borderRadius: 0, boxShadow: "none" }}
                    >
                        <CardActionArea onClick={() => router.history.push(propertyItemPathname)}>
                            {renderPropertyItemCardContentMobile(title)}
                            {hasImage ? (
                                <>
                                    <PrivateBlob src={image.split(".").pop() !== "pdf" ? image : thumbnail} title={subTitle} cardMedia={true} />
                                </>
                            ) : (
                                <React.Fragment />
                            )}
                        </CardActionArea>
                    </Card>
                )}
            </>
        );
    };

    const renderItemCardContentDekstop = () => {
        let items = [
            { title: "Build completion", date: `Target date: ${viewModelProperty.BuildTargetDate}`, icon: Home, ordinal: 0 },
            { title: "Contract exchange", date: `Target date: ${viewModelProperty.ContractTargetDate}`, icon: Contract, ordinal: 3 },
            { title: "Your documents", date: `${viewModel.DocumentsSummary}`, icon: Document, ordinal: 5 },
        ];

        if (viewModel.canViewIsChoicesToPlot) {
            items.push(
                { title: "Choices complete", date: `Target date: ${viewModelProperty.ChoicesTargetDate}`, icon: Touch, ordinal: 1 },
                { title: "Legal completion", date: `Target date: ${viewModelProperty.LegalTargetDate}`, icon: Legal, ordinal: 2 },
            );
        }

        if (viewModel.canViewSnags && viewModel.canViewReportSnags) {
            items.push({ title: "Current snags", date: `${viewModel.SnagsSummary}`, icon: Tool, ordinal: 4 });
        }

        return (
            <Box className={mainPageStyles.pageTemplate}>
                <Box className={mainPageStyles.cardContainer}>
                    {items
                        .sort((a, b) => a.ordinal - b.ordinal)
                        .map((item, index) => (
                            <Box key={index} className={mainPageStyles.chooseCard}>
                                <Box className={mainPageStyles.chooseCardIcon}>
                                    <item.icon />
                                </Box>

                                <Typography className={mainPageStyles.chooseCardTitle}>{item.title.toUpperCase()}</Typography>

                                <Box className={mainPageStyles.chooseCardDateRow}>
                                    <Typography variant="body2">{item.date}</Typography>
                                </Box>
                            </Box>
                        ))}
                </Box>

                <Box className={mainPageStyles.cardWithInfo}>
                    {renderStatusCards()}
                    {viewModel.canViewIsChoicesToPlot && (
                        <Box className={mainPageStyles.infoCard}>
                            <Box className={mainPageStyles.infoCardText}>
                                <Typography className={mainPageStyles.infoCardTitle}>REMINDER</Typography>
                                <Typography className={mainPageStyles.infoCardSubTitle}>Payments outstanding</Typography>
                                <Box className={mainPageStyles.infoCardItems}>
                                    <Typography>
                                        <span>RESERVATION FEE:</span>
                                        {viewModelProperty.returnOutstandingReservationFee}
                                    </Typography>
                                    <Typography>
                                        <span>CHOICES:</span>
                                        {viewModelProperty.returnOutstandingChoices}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={mainPageStyles.infoCardIcon} onClick={handleRedirectToPayments}>
                                <CircleRightArrow />
                            </Box>
                        </Box>
                    )}

                    <DialogModal
                        className={mainPageStyles.smallmodel}
                        open={openDialog}
                        title={"Sales status:"}
                        subtitle={viewModel.Summary.progressName}
                        onClose={() => handleStatusModal(false)}
                        icon={Keys}
                    >
                        <ProgressView viewModelProgress={viewModelProgress} />
                    </DialogModal>

                    <DialogModal
                        className={mainPageStyles.smallmodel}
                        open={openBuildDialog}
                        title={"Build status:"}
                        subtitle={viewModel.Summary.buildStatusName}
                        onClose={() => handleBuildStatusModal(false)}
                        icon={Brickwall}
                    >
                        <Inspection />
                    </DialogModal>
                </Box>
                {/* Snag outstanding details card  */}
                {viewModel.canViewSnags && viewModel.canViewReportSnags && (
                    <Box className={mainPageStyles.infoCardRow}>
                        <Box className={mainPageStyles.infoCard}>
                            <Box className={`${mainPageStyles.infoCardText} infoWarrantyCard`}>
                                <Typography className={`${mainPageStyles.infoCardTitle} infoCardTitle`}>REMINDER</Typography>
                                <Typography className={mainPageStyles.infoCardSubTitle}>Snags outstanding</Typography>
                                <Box className={mainPageStyles.infoCardItems}>
                                    <Typography>
                                        <span>SNAGS REPORTED:</span>
                                        {viewModel.model.snagCount}
                                    </Typography>
                                    <Typography>
                                        <span>EARLIEST REPORTED:</span>
                                        {viewModel.SnagsDate}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={mainPageStyles.infoCardIcon} onClick={handleRedirectToSnags}>
                                <CircleRightArrow />
                            </Box>
                        </Box>

                        {viewModelProperty.hasLegalStatus && viewModelProperty.showFullReservationFormV2 && (
                            <Box className={mainPageStyles.infoCard}>
                                <WarrantyLogo className={mainPageStyles.infoCardImage} providerName={viewModel.model.warrantyProviderName} />

                                <Box className={`${mainPageStyles.infoCardText} infoWarrantyCard`}>
                                    <Typography className={`${mainPageStyles.infoCardTitle} infoCardTitle`}>Warranty</Typography>
                                    <Typography className={mainPageStyles.infoCardSubTitle}>
                                        {formatSalesStatusLite(viewModelProperty, viewModelProperty.getWarrantyCountdownInYears)}
                                    </Typography>
                                    <Box className={mainPageStyles.infoCardItems}>
                                        <Box className="iconBox" component="span">
                                            <Phone height={"20px"} width={"20px"} />
                                            <Typography component="span">
                                                <span>{viewModel.model.warrantyProviderContactNumber}</span>
                                            </Typography>
                                        </Box>
                                        <Box className="iconBox" component="span">
                                            <Globe height={"20px"} width={"20px"} />
                                            <Typography component="span">
                                                <span>{viewModel.model.warrantyProviderEmail}</span>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const renderItemCardContentMobile = () => {
        let items = [
            { title: "Build completion", date: `Target date: ${viewModelProperty.BuildTargetDate}`, icon: Home, ordinal: 0 },
            { title: "Contract exchange", date: `Target date: ${viewModelProperty.ContractTargetDate}`, icon: Contract, ordinal: 3 },
            { title: "Your documents", date: `${viewModel.DocumentsSummary}`, icon: Document, ordinal: 5 },
        ];

        if (viewModel.canViewIsChoicesToPlot) {
            items.push(
                { title: "Choices complete", date: `Target date: ${viewModelProperty.ChoicesTargetDate}`, icon: Touch, ordinal: 1 },
                { title: "Legal completion", date: `Target date: ${viewModelProperty.LegalTargetDate}`, icon: Legal, ordinal: 2 },
            );
        }

        if (viewModel.canViewSnags && viewModel.canViewReportSnags) {
            items.push({ title: "Current snags", date: `${viewModel.SnagsSummary}`, icon: Tool, ordinal: 4 });
        }

        return (
            <Box className={mainPageStyles.pageTemplate}>
                {renderStatusCards()}
                <Box className={mainPageStyles.cardContainer}>
                    {items
                        .sort((a, b) => a.ordinal - b.ordinal)
                        .map((item, index) => (
                            <Box key={index} className={mainPageStyles.chooseCard}>
                                <Box className={mainPageStyles.chooseCardIcon}>
                                    <item.icon />
                                </Box>

                                <Typography className={mainPageStyles.chooseCardTitle}>{item.title.toUpperCase()}</Typography>

                                <Box className={mainPageStyles.chooseCardDateRow}>
                                    <Typography variant="body2">Target date:</Typography>
                                    <Typography variant="body2">{item.date}</Typography>
                                </Box>
                            </Box>
                        ))}
                </Box>
                <Box className={mainPageStyles.cardWithInfo}>
                    <Box className={mainPageStyles.infoCard}>
                        <Box className={mainPageStyles.infoCardText}>
                            <Typography className={mainPageStyles.infoCardTitle}>REMINDER</Typography>
                            <Typography className={mainPageStyles.infoCardSubTitle}>Payments outstanding</Typography>
                            <Box className={mainPageStyles.infoCardItems}>
                                <Typography>
                                    <span>RESERVATION FEE:</span>
                                    {viewModelProperty.getReservationStripePaymentRequest}
                                </Typography>
                                <Typography>
                                    <span>CHOICES:</span>
                                    {viewModelProperty.getChoicesStripePaymentRequest}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={mainPageStyles.infoCardIcon} onClick={handleRedirectToPayments}>
                            <CircleRightArrow />
                        </Box>
                    </Box>
                </Box>

                {/* Snag outstanding details card  */}
                {viewModel.canViewSnags && viewModel.canViewReportSnags && (
                    <Box className={mainPageStyles.infoCardRow}>
                        <Box className={mainPageStyles.infoCard}>
                            <Box className={mainPageStyles.infoCardText}>
                                <Typography className={mainPageStyles.infoCardTitle}>REMINDER</Typography>
                                <Typography className={mainPageStyles.infoCardSubTitle}>Snags outstanding</Typography>
                                <Box className={mainPageStyles.infoCardItems}>
                                    <Typography>
                                        <span>SNAGS REPORTED:</span>
                                        {viewModel.model.snagCount}
                                    </Typography>
                                    <Typography>
                                        <span>EARLIEST REPORTED:</span>
                                        {viewModel.SnagsDate}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={mainPageStyles.infoCardIcon} onClick={handleRedirectToSnags}>
                                <CircleRightArrow />
                            </Box>
                        </Box>

                        {viewModelProperty.hasLegalStatus && viewModelProperty.showFullReservationFormV2 && (
                            <Box className={mainPageStyles.infoCard}>
                                <WarrantyLogo className={mainPageStyles.infoCardImage} providerName={viewModel.model.warrantyProviderName} />

                                <Box className={`${mainPageStyles.infoCardText} infoWarrantyCard`}>
                                    <Typography className={`${mainPageStyles.infoCardTitle} infoCardTitle`}>Warranty</Typography>
                                    <Typography className={mainPageStyles.infoCardSubTitle}>
                                        {formatSalesStatusLite(viewModelProperty, viewModelProperty.getWarrantyCountdownInYears)}
                                    </Typography>
                                    <Box className={mainPageStyles.infoCardItems}>
                                        <Box className="iconBox" component="span">
                                            <Phone height={"20px"} width={"20px"} />

                                            <span>{viewModel.model.warrantyProviderContactNumber}</span>
                                        </Box>
                                        <Box className="iconBox" component="span">
                                            <Globe height={"20px"} width={"20px"} />

                                            <span>{viewModel.model.warrantyProviderEmail}</span>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Sales status */}
                <Drawer anchor="bottom" open={drawerOpen} onClose={() => handleStatusModal(false)} className={mainPageStyles.drawerPaperMobile}>
                    <Box>
                        <Box className={mainPageStyles.drawerPaperMobileHeader}>
                            <Box className={mainPageStyles.drawerPaperMobileHeaderInner}>
                                <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Typography component="span" className={mainPageStyles.drawerPaperMobileHeaderIcon}>
                                        <Keys />
                                    </Typography>
                                    <Box className={mainPageStyles.drawerPaperMobileHeaderTitle}>
                                        <Typography component="h4">Sales status:</Typography>
                                        <Typography component="p">{viewModel.Summary.progressName}</Typography>
                                    </Box>
                                </Box>
                                <Typography component="span" className={mainPageStyles.drawerPaperMobileHeaderCloseIcon} onClick={() => handleStatusModal(false)}>
                                    <CloseIcon />
                                </Typography>
                            </Box>
                        </Box>
                        <ProgressView viewModelProgress={viewModelProgress} />
                    </Box>
                </Drawer>

                {/* Build status */}
                <Drawer anchor="bottom" open={buildDrawerOpen} onClose={() => handleBuildStatusModal(false)} className={mainPageStyles.drawerPaperMobile}>
                    <Box>
                        <Box className={mainPageStyles.drawerPaperMobileHeader}>
                            <Box className={mainPageStyles.drawerPaperMobileHeaderInner}>
                                <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Typography component="span" className={mainPageStyles.drawerPaperMobileHeaderIcon}>
                                        <Brickwall />
                                    </Typography>
                                    <Box className={mainPageStyles.drawerPaperMobileHeaderTitle}>
                                        <Typography component="h4">Build status:</Typography>
                                        <Typography component="p">{viewModel.Summary.buildStatusName}</Typography>
                                    </Box>
                                </Box>
                                <Typography component="span" className={mainPageStyles.drawerPaperMobileHeaderCloseIcon} onClick={() => handleBuildStatusModal(false)}>
                                    <CloseIcon />
                                </Typography>
                            </Box>
                        </Box>
                        <Inspection />
                    </Box>
                </Drawer>
            </Box>
        );
    };

    const renderStatusCards = () => {
        const salesStatus = viewModelProgress.SalesStatus(viewModelProperty.model.salesStatusId);
        let salesStatusName = formatSalesStatus(viewModelProperty, salesStatus!, viewModelProperty.getWarrantyCountdownInYears);
        let salesStatusColor = "#DB9214";

        if (salesStatusName == "Invalid property") {
            if (viewModelProperty.model.salesStatusName == "Legal completion" || viewModelProperty.model.salesStatusName == "10 year warranty") {
                salesStatusName = "Completed";
                salesStatusColor = "#5BB44E";
            } else {
                salesStatusName = viewModelProperty.model.salesStatusName;
            }
        }

        const cards = [
            {
                title: "BUILD STATUS",
                subtitle: `${viewModel.Summary.buildStatusName}`,
                icon: Brickwall,
                color: `${viewModel.Summary.buildStatusColour}`,
            },
            {
                title: "SALES STATUS",
                subtitle: `${salesStatusName}`,
                icon: Keys,
                color: `${salesStatus ? salesStatus.activityColour : salesStatusColor}`,
            },
        ];

        return (
            <Box className={mainPageStyles.topCardsContainer}>
                {cards.map((card, index) => (
                    <Box
                        key={index}
                        className={mainPageStyles.topCard}
                        style={{ backgroundColor: `${card.color}`, cursor: "pointer" }}
                        onClick={index == 1 ? () => handleStatusModal(true) : () => handleBuildStatusModal(true)}
                    >
                        <Box className={mainPageStyles.cardIcon}>
                            <card.icon style={{ maxWidth: "100%", maxHeight: "100%" }} />
                        </Box>
                        <Typography className={mainPageStyles.topCardTitle}>{card.title}</Typography>
                        <Typography className={mainPageStyles.topCardSubtitle}>{card.subtitle}</Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderItems = () => {
        return (
            <Box className="cards-container" m={0}>
                {renderPropertyItem(
                    viewModel.Summary.houseTypeImageUrl,
                    viewModel.PropertySummary,
                    viewModel.Summary.houseTypeName,
                    viewModel.navigateToPropertyPathname,
                    viewModel.Summary.houseTypeThumbnailUrl,
                )}

                {isDesktop ? renderItemCardContentDekstop() : renderItemCardContentMobile()}
            </Box>
        );
    };

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${mainPageStyles.root} ${pageAnimationStyles.pageWrapper}`}>
            <Box className={`${dashboardPageStyles.workspace} ${mainPageStyles.workspace}`}>
                {viewModel.ApiStatus === ApiStatus.success && <React.Fragment>{renderItems()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>
        </Box>
    ));
};
